"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { apiRequest } from "./utils/api";
import { ProductCard } from "./components/ProductCard";
import { ArrowRight, Laptop, Smartphone, Headphones, Watch, Sparkles, ChevronDown } from "lucide-react";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  stock: number;
  images: string[];
  category: string;
  isFeatured?: boolean;
}

interface HomepageSectionConfig {
  id: string;
  title: string;
  category: string;
  allowSoldOut: boolean;
  isActive: boolean;
}

const HomepageCategorySection: React.FC<{ section: HomepageSectionConfig }> = ({ section }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadInitialProducts = async () => {
      setIsLoading(true);
      try {
        let url = `/products?category=${encodeURIComponent(section.category)}&limit=6&page=1&preferInStock=true`;
        if (!section.allowSoldOut) {
          url += `&excludeSoldOut=true`;
        }
        const res = await apiRequest(url);
        if (isMounted && res?.success && res.data) {
          setProducts(res.data);
          setTotal(res.meta?.total ?? res.data.length);
        }
      } catch {
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadInitialProducts();
    return () => {
      isMounted = false;
    };
  }, [section.category, section.allowSoldOut]);

  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    const nextPage = page + 1;
    setIsLoadingMore(true);
    try {
      let url = `/products?category=${encodeURIComponent(section.category)}&limit=6&page=${nextPage}&preferInStock=true`;
      if (!section.allowSoldOut) {
        url += `&excludeSoldOut=true`;
      }
      const res = await apiRequest(url);
      if (res?.success && res.data && res.data.length > 0) {
        setProducts((prev) => [...prev, ...res.data]);
        setPage(nextPage);
      }
    } catch {
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Do not show an empty category section if it has no available products
  if (!isLoading && products.length === 0) {
    return null;
  }

  const hasMore = total !== null && products.length < total;

  return (
    <section className="py-16 bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-900">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div className="text-left">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
              {section.title}
            </h2>
            <p className="text-sm text-zinc-500 mt-1.5">
              Handpicked products in <span className="font-semibold text-violet-600 dark:text-violet-400">{section.category}</span>
            </p>
          </div>
          <Link
            href={`/shop?category=${encodeURIComponent(section.category)}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-500 transition-colors"
          >
            View All
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse flex flex-col gap-4 border border-zinc-200 dark:border-zinc-900 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950">
                <div className="aspect-square bg-zinc-200 dark:bg-zinc-900 rounded-xl" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-900 rounded-md w-2/3" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-900 rounded-md w-1/2 mt-auto" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="inline-flex items-center gap-2 rounded-full border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-6 py-2.5 text-xs sm:text-sm font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-850 transition-colors shadow-sm disabled:opacity-50"
                >
                  {isLoadingMore ? (
                    <>
                      <span className="h-4 w-4 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <>
                      Load More Products
                      <ChevronDown className="h-4 w-4 text-violet-400" />
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categorySections, setCategorySections] = useState<HomepageSectionConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHomepageData = async () => {
      try {
        const [featRes, secRes] = await Promise.all([
          apiRequest("/products?isFeatured=true&limit=8"),
          apiRequest("/homepage-sections"),
        ]);

        if (featRes?.success && featRes.data) {
          setFeaturedProducts(featRes.data);
        }
        if (secRes?.success && secRes.data) {
          setCategorySections(secRes.data);
        }
      } catch {
      } finally {
        setIsLoading(false);
      }
    };
    fetchHomepageData();
  }, []);

  return (
    <div className="flex flex-col w-full pb-20">
      
      {/* Premium Hero Section */}
      <section className="relative overflow-hidden bg-zinc-950 text-white py-24 md:py-32">
        {/* Glow Effects */}
        <div className="absolute top-0 right-1/4 h-[300px] w-[300px] rounded-full bg-violet-600/20 blur-[100px]" />
        <div className="absolute bottom-0 left-1/4 h-[250px] w-[250px] rounded-full bg-indigo-600/20 blur-[90px]" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            {/* Hero Left Content */}
            <div className="md:col-span-7 flex flex-col gap-6 text-left">
              <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-violet-500/10 px-3.5 py-1 text-xs font-semibold text-violet-400 border border-violet-500/20">
                <Sparkles className="h-3.5 w-3.5" />
                Next-Gen Gadgets Collection
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
                Experience Tech at its{" "}
                <span className="bg-gradient-to-r from-violet-400 via-indigo-400 to-cyan-400 text-transparent bg-clip-text">
                  Absolute Finest
                </span>
              </h1>
              
              <p className="text-lg text-zinc-400 max-w-xl leading-relaxed">
                Step into the future of lifestyle tech. Discover our handpicked collections of hyper-fast smartphones, immersive audio, smart home gear, and premium computing.
              </p>
              
              <div className="flex flex-wrap gap-4 mt-2">
                <Link
                  href="/shop"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black hover:bg-zinc-100 transition-colors shadow-lg"
                >
                  Shop Catalog
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  href="/shop?isFeatured=true"
                  className="inline-flex items-center justify-center rounded-full border border-zinc-800 bg-zinc-900/60 px-6 py-3 text-sm font-semibold text-zinc-300 hover:border-zinc-700 transition-colors"
                >
                  Featured Offers
                </Link>
              </div>
            </div>

            {/* Hero Right Graphic - Glassmorphic Tech Interface Mock */}
            <div className="md:col-span-5 relative flex items-center justify-center h-[350px] md:h-[400px]">
              {/* Background glowing orb */}
              <div className="absolute inset-0 bg-indigo-500/15 rounded-full blur-[80px] animate-pulse pointer-events-none" />
              
              {/* Futuristic grid mesh background */}
              <div className="absolute h-64 w-64 border border-zinc-850 rounded-full flex items-center justify-center pointer-events-none">
                <div className="absolute h-48 w-48 border border-zinc-850/60 rounded-full border-dashed" />
                <div className="absolute h-32 w-32 border border-zinc-850/30 rounded-full" />
              </div>

              {/* Central Premium Gadget Hub Card (Floating) */}
              <div className="relative w-72 bg-zinc-900/65 backdrop-blur-md border border-zinc-800 rounded-3xl p-6 shadow-2xl shadow-violet-500/5 hover:-translate-y-1 hover:border-violet-500/50 transition-all duration-300">
                <div className="flex items-center justify-between border-b border-zinc-850 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-400">Torongo Hub OS v2.4</span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded-full">Active</span>
                </div>

                <div className="mt-4 space-y-4">
                  {/* Laptop Card snippet */}
                  <div className="flex items-center justify-between bg-zinc-950/50 border border-zinc-850 p-2.5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-indigo-500/10 text-indigo-400">
                        <Laptop className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <span className="text-xs font-bold text-white block">Elite Computers</span>
                        <span className="text-[9px] text-zinc-500 block">System performance peak</span>
                      </div>
                    </div>
                    <span className="text-xs font-mono font-bold text-indigo-400">99%</span>
                  </div>

                  {/* Audio Card snippet */}
                  <div className="flex items-center justify-between bg-zinc-950/50 border border-zinc-850 p-2.5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-pink-500/10 text-pink-400">
                        <Headphones className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <span className="text-xs font-bold text-white block">Hi-Fi Audio</span>
                        <span className="text-[9px] text-zinc-500 block">Surround acoustic mode</span>
                      </div>
                    </div>
                    {/* Tiny waves graph animation mock */}
                    <div className="flex items-center gap-0.5 h-3 shrink-0">
                      <span className="w-0.5 h-3 bg-pink-500 rounded-full animate-pulse" />
                      <span className="w-0.5 h-1 bg-pink-500 rounded-full animate-pulse" />
                      <span className="w-0.5 h-4 bg-pink-500 rounded-full animate-pulse" />
                      <span className="w-0.5 h-2 bg-pink-500 rounded-full animate-pulse" />
                    </div>
                  </div>

                  {/* Smart Watch snippet */}
                  <div className="flex items-center justify-between bg-zinc-950/50 border border-zinc-850 p-2.5 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-amber-500/10 text-amber-400">
                        <Watch className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <span className="text-xs font-bold text-white block">Wearable Status</span>
                        <span className="text-[9px] text-zinc-500 block">Health sync completed</span>
                      </div>
                    </div>
                    <span className="h-4.5 w-4.5 rounded-full border-2 border-amber-500 border-t-transparent animate-spin shrink-0" />
                  </div>
                </div>

                <div className="mt-6 flex justify-between items-center text-[10px] text-zinc-550 font-semibold border-t border-zinc-855 pt-3">
                  <span>Store Warranty Included</span>
                  <span className="text-violet-400">Genuine Gear</span>
                </div>
              </div>

              {/* Floating secondary cards */}
              <div className="absolute top-8 right-6 bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl shadow-xl flex items-center gap-2.5 hover:-translate-y-1 hover:border-violet-400 transition-all duration-300">
                <div className="h-8 w-8 rounded-lg bg-pink-500/10 text-pink-400 flex items-center justify-center shrink-0">
                  <Headphones className="h-4 w-4" />
                </div>
                <div className="min-w-0 text-left">
                  <span className="text-[10px] font-bold text-white truncate block">Sony WH-1000XM5</span>
                  <span className="text-[8px] text-emerald-500 font-bold block mt-0.5">৳399 &middot; In Stock</span>
                </div>
              </div>

              <div className="absolute bottom-8 left-4 bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl shadow-xl flex items-center gap-2.5 hover:-translate-y-1 hover:border-cyan-400 transition-all duration-300">
                <div className="h-8 w-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0">
                  <Smartphone className="h-4 w-4" />
                </div>
                <div className="min-w-0 text-left">
                  <span className="text-[10px] font-bold text-white truncate block">iPhone 15 Pro Max</span>
                  <span className="text-[8px] text-violet-400 font-bold block mt-0.5">Top Choice Category</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 1. Featured Products Section */}
      <section className="py-16 bg-white dark:bg-zinc-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="text-left">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
                Featured Products
              </h2>
              <p className="text-sm text-zinc-500 mt-1.5">
                Explore our top-tier devices, representing the best in premium specifications.
              </p>
            </div>
            <Link
              href="/shop"
              className="inline-flex items-center gap-1 text-sm font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-500 transition-colors"
            >
              See all products
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse flex flex-col gap-4 border border-zinc-200 dark:border-zinc-900 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-950">
                  <div className="aspect-square bg-zinc-200 dark:bg-zinc-900 rounded-xl" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-900 rounded-md w-2/3" />
                  <div className="h-4 bg-zinc-200 dark:bg-zinc-900 rounded-md w-1/2 mt-auto" />
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-10">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl mt-10 max-w-md mx-auto">
              <h3 className="font-semibold text-lg">No featured products found</h3>
              <p className="text-sm text-zinc-500 mt-1">
                Wait for the admin dashboard to feature products or seed your database!
              </p>
              <Link
                href="/dashboard"
                className="inline-flex items-center gap-2 mt-4 rounded-full bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500 transition-colors"
              >
                Go to Dashboard
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* 2. Dynamic Homepage Category Sections */}
      {categorySections.map((section) => (
        <HomepageCategorySection key={section.id} section={section} />
      ))}
    </div>
  );
}
